import SectionCard from '@components/Common/EntityWorkspace/SectionCard';
import FormField from '@components/Common/FormField';
import type { TopQuestion } from '@constants/topQuestions';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import './TopQuestionsCard.scss';

export interface AnswerPair {
  key: string;
  value: string;
}

interface TopQuestionsCardProps {
  title: string;
  questions: TopQuestion[];
  /** Stored answers, as they came back from the record. Unknown keys are ignored. */
  answers: AnswerPair[];
  /** Receives every question's key, including the blank ones. */
  onSave: (answers: AnswerPair[]) => Promise<void> | void;
  saving?: boolean;
  disabled?: boolean;
}

/**
 * A fixed question set with a free-text answer each, read-only until edited.
 *
 * Shared rather than written per workspace. `InternalFieldsCard` — the closest existing card —
 * lives inside `ApplicationWorkspace` and was copied by hand into `JobSeekerWorkspace`; the two
 * have since drifted. This one is used by both the candidate and the company workspace, so a
 * change to how an answer is captured happens once.
 *
 * **Saves all five keys, blanks included.** The record stores whatever it is sent, so omitting
 * an empty answer would make "asked, and they would not say" indistinguishable from "never
 * asked" — which is exactly the distinction a recruiter reading someone else's account needs.
 */
const TopQuestionsCard: React.FC<TopQuestionsCardProps> = ({
  title,
  questions,
  answers,
  onSave,
  saving = false,
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const stored = React.useMemo(() => {
    const map: Record<string, string> = {};
    (answers ?? []).forEach((a) => {
      if (a?.key) map[a.key] = a.value ?? '';
    });
    return map;
  }, [answers]);

  // Re-seed when the record reloads, but not while someone is mid-edit: a refetch landing
  // during typing would otherwise silently discard what they had entered.
  useEffect(() => {
    if (!editing) setDraft(stored);
  }, [stored, editing]);

  const answeredCount = questions.filter((q) => (stored[q.key] ?? '').trim()).length;

  const handleSave = async () => {
    await onSave(questions.map((q) => ({ key: q.key, value: draft[q.key] ?? '' })));
    setEditing(false);
  };

  return (
    <SectionCard
      title={title}
      icon={<QuizOutlinedIcon />}
      meta={`${answeredCount} of ${questions.length} answered`}
      editing={editing}
      onEdit={disabled ? undefined : () => setEditing(true)}
      onCancel={() => {
        setDraft(stored);
        setEditing(false);
      }}
      onSave={saving ? undefined : handleSave}
    >
      <Box className="top-questions">
        {questions.map((q, index) => {
          const answer = editing ? draft[q.key] ?? '' : stored[q.key] ?? '';
          return (
            <Box key={q.key} className="top-questions__item">
              <Typography
                component="p"
                id={`tq-label-${q.key}`}
                className="top-questions__question"
              >
                {index + 1}. {q.question}
              </Typography>
              {q.hint && (
                <Typography component="p" className="top-questions__hint">
                  {q.hint}
                </Typography>
              )}
              {editing ? (
                <FormField
                  required={false}
                  multiline
                  name={`top-question-${q.key}`}
                  label=""
                  placeholder="Not answered yet"
                  value={answer}
                  // Named by its question. `label=""` suppresses FormField's own label, so
                  // without this a screen reader reaching the textarea hears only the
                  // placeholder — which disappears as soon as there is an answer.
                  textFieldProps={{
                    rows: undefined,
                    minRows: 2,
                    'aria-labelledby': `tq-label-${q.key}`,
                  }}
                  onChange={(_n, v) => setDraft((d) => ({ ...d, [q.key]: String(v) }))}
                />
              ) : (
                <Typography
                  component="p"
                  className={`top-questions__answer${answer ? '' : ' is-empty'}`}
                >
                  {answer || 'Not answered yet'}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
};

export default TopQuestionsCard;
